import { KGDataFrameProto } from "build-ts/dataframe";
import { FilterProto } from "build-ts/metadata";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import React, { useMemo, useState } from "react";
import DataFramePlan from "components/agentChat/DataFramePlan";
import {
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Tooltip,
  Text,
  Button,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { FaExclamationTriangle, FaChevronDown, FaChevronUp, FaRegFileExcel, FaFilter, FaEye, FaEyeSlash } from "react-icons/fa";
import { PiIntersectFill } from "react-icons/pi";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";
import { FaTableList } from "react-icons/fa6";
import { IoCopyOutline } from "react-icons/io5";
import FormattedPandasTable from "./FormattedPandasTable";
import { extractRawDataFromDataFrame, copyRawTableToClipboard, downloadRawTableAsExcel } from "components/analyse/data_table/dataTableUtils";
import { populateArtifact } from "services/DataService";
import { FiDatabase } from "react-icons/fi";
import { SiGooglebigquery } from "react-icons/si";
import { DownloadIcon } from "@chakra-ui/icons";
import { MdAdd, MdRemove } from "react-icons/md";
import { TbMathFunction } from "react-icons/tb";
import ArtifactPanelHeader from "components/agentChat/ArtifactPanelHeader";

type TabId = "data" | "plan";

interface DataFrameDisplayComponentProps {
  name?: string;
  data: KGDataFrameProto;
  isSelected?: boolean;
  onClose?: () => void;
  /** No-op in the panel context — collapse is handled by the artifact strip. */
  collapseSignal?: number;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
}

const DataFrameDisplayComponent: React.FC<DataFrameDisplayComponentProps> = ({ name, data, isSelected, onClose, collapseSignal, isExpanded, onExpandToggle }) => {
  const [displayedData, setDisplayedData] = useState<KGDataFrameProto>(data);
  const [activeTab, setActiveTab] = useState<TabId>("data");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);
  // number of rows in the dataframe
  const [numberOfRows, setNumberOfRows] = useState(0);

  // Whether there are any hidden columns to toggle
  const hasHiddenColumns = useMemo(
    () => (displayedData.ordered_columns ?? []).some((col) => col.is_visible === false),
    [displayedData]
  );

  const queryFilters: FilterProto[] = useMemo(
    () => displayedData?.kg_observation?.filters || [],
    [displayedData]
  );

  // Track which expression filters are checked
  const [checkedExpressionFilters, setCheckedExpressionFilters] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    (data.expression_filters ?? []).forEach((filter) => {
      initial[filter] = true; // All checked by default
    });
    return initial;
  });

  // Track which filters were active at the time of the last successful API fetch.
  // Used to detect if the current selection has drifted from the displayed data.
  const [lastFetchedFilters, setLastFetchedFilters] = useState<string[]>(data.expression_filters ?? []);
  const [isRecomputing, setIsRecomputing] = useState(false);

  const hasExpressionFiltersChanged = useMemo(() => {
    const current = Object.entries(checkedExpressionFilters)
      .filter(([, isChecked]) => isChecked)
      .map(([filter]) => filter);
    if (current.length !== lastFetchedFilters.length) return true;
    const lastSet = new Set(lastFetchedFilters);
    return current.some((f) => !lastSet.has(f));
  }, [checkedExpressionFilters, lastFetchedFilters]);

  const handleGetData = async () => {
    const selectedFilters = Object.entries(checkedExpressionFilters)
      .filter(([, isChecked]) => isChecked)
      .map(([filter]) => filter);

    const modifiedData: KGDataFrameProto = {
      ...data,
      expression_filters: selectedFilters,
    };

    const artifactRequest = ArtifactProto.create({
      artifact_type: ArtifactType.KG_DATAFRAME,
      kg_dataframe: modifiedData
    });

    setIsRecomputing(true);
    setActiveTab("data");
    try {
      const response = await populateArtifact(artifactRequest);
      const dataframeData = response?.kg_dataframe;
      if (dataframeData) {
        setDisplayedData(dataframeData);
        setLastFetchedFilters(selectedFilters);
      }
    } finally {
      setIsRecomputing(false);
    }
  };

  // Process aggregate columns (is_aggregate_expression is true)
  const aggregateColumns = useMemo(() => {
    return (displayedData.ordered_columns ?? [])
      .filter((column) => column.is_aggregate_expression)
      .map((column) => {
        const name = column.output_column_name || "";
        const eval_expression = column.eval_expression || "";

        let value: any = null;
        if (column.data_value_json_array) {
          try {
            const sanitizedJson = column.data_value_json_array
              .replace(/\bNaN\b/g, "null")
              .replace(/\bInfinity\b/g, "null")
              .replace(/\b-Infinity\b/g, "null");
            const parsedValues = JSON.parse(sanitizedJson);
            value = Array.isArray(parsedValues) ? parsedValues[0] : parsedValues;
          } catch (error) {
            console.error(`Failed to parse aggregate data for column ${name}:`, error);
          }
        }

        return {
          name,
          eval_expression,
          value: value !== null && value !== undefined ? String(value) : "N/A",
        };
      });
  }, [displayedData]);

  const tabs: { id: TabId; label: string; icon: React.ElementType; iconSize?: number }[] = [
    { id: "data", label: "Data", icon: FiDatabase },
    { id: "plan", label: "Query Plan", icon: SiGooglebigquery, iconSize: 3.5 }

  ];

  const headerActions = (
    <>
      {numberOfRows > 0 && (
        <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
          {numberOfRows} {numberOfRows === 1 ? "row" : "rows"}
        </Text>
      )}

      {hasHiddenColumns && (
        <Tooltip
          label={showHiddenColumns ? "Hide Intermediate Columns" : "Show Intermediate Columns"}
          placement="bottom"
          hasArrow
          fontSize="xs"
        >
          <IconButton
            icon={<Icon as={showHiddenColumns ? FaEye : FaEyeSlash} boxSize={4} />}
            aria-label={showHiddenColumns ? "Hide Intermediate Columns" : "Show Intermediate Columns"}
            size="xs"
            color={showHiddenColumns ? "purple.600" : "gray.700"}
            bg={showHiddenColumns ? "purple.50" : "transparent"}
            _hover={{ bg: "purple.50", color: "purple.600" }}
            onClick={() => setShowHiddenColumns((prev) => !prev)}
          />
        </Tooltip>
      )}

      <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
        <MenuButton
          as={IconButton}
          size="xs"
          color="gray.600"
          bg="transparent"
          _hover={{ bg: "gray.50", color: "gray.800" }}
          aria-label="Settings"
          icon={
            <HStack spacing={0}>
              <Icon as={DownloadIcon} boxSize={5} />
            </HStack>
          }
          title="Settings"
        />
        <Portal>
          <MenuList
            minW="120px"
            zIndex={1000}
            shadow="lg"
            border="1px solid"
            borderColor="gray.200"
            p={1}
            position="relative"
          >
            <MenuItem
              icon={<Icon as={IoCopyOutline} boxSize={4} />}
              onClick={() => {
                const rawData = extractRawDataFromDataFrame(displayedData);
                copyRawTableToClipboard(rawData);
              }}
              fontSize="sm"
            >
              Copy to clipboard
            </MenuItem>
            <MenuItem
              icon={<Icon as={FaRegFileExcel} boxSize={4} />}
              onClick={() => {
                const rawData = extractRawDataFromDataFrame(displayedData);
                downloadRawTableAsExcel(rawData, "dataframe_export.xlsx");
              }}
              fontSize="sm"
            >
              Download Excel file
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>

      {displayedData.error_messages && displayedData.error_messages.length > 0 && (
        <Tooltip
          label={
            <Box as="ul" pl={4} maxW="300px" fontSize="xs">
              {displayedData.error_messages.map((error, index) => (
                <Box as="li" key={index}>{error}</Box>
              ))}
            </Box>
          }
          placement="bottom"
          hasArrow
          bg="red.600"
        >
          <IconButton
            icon={<Icon as={FaExclamationTriangle} boxSize={4} />}
            aria-label="Show errors"
            size="xs"
            color="red.500"
            bg="transparent"
            _hover={{ bg: "red.50", color: "red.600" }}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      )}
    </>
  );

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      width="100%"
      flex="1 1 0"
      minH={0}
      overflow="hidden"
    >
      <ArtifactPanelHeader
        icon={FaTableList}
        iconBg="white"
        iconColor="purple.700"
        name={name ?? ""}
        typeLabel="Data Table"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      {/* Tab body scrolls; header (title + tabs) stays put */}
      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        overflowX="hidden"
        onWheel={(e) => e.stopPropagation()}
        sx={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
        }}
      >
        {/* Data Tab */}
        {activeTab === "data" && (
          <VStack align="stretch" spacing={2} textAlign="left" px={0} py={1}>
            {/* Collapsible Applied Filters strip */}
            {queryFilters.length > 0 && (
              <Box>
                <HStack
                  px={3}
                  py={2}
                  cursor="pointer"
                  onClick={() => setIsFiltersExpanded((prev) => !prev)}
                  justifyContent="space-between"
                  _hover={{ bg: "gray.100" }}
                  transition="background 0.15s"
                >
                  <HStack spacing={1.5}>
                    <Icon as={FaFilter} boxSize={3} color="purple.600" />
                    <Text fontSize="xs" fontWeight="700" color="gray.700" m={0}>
                      FILTERS
                    </Text>
                    <Text fontSize="xs" color="gray.500" m={0}>({queryFilters.length})</Text>
                  </HStack>
                  <Icon
                    as={isFiltersExpanded ? FaChevronUp : FaChevronDown}
                    boxSize={2.5}
                    color="gray.500"
                  />
                </HStack>
                <Collapse in={isFiltersExpanded} animateOpacity>
                  <Box display="flex" flexWrap="wrap" gap={2} px={2} py={1} bg="white">
                    {queryFilters.map((filter, i) => (
                      <Tag
                        key={i}
                        size="sm"
                        borderRadius="md"
                        variant="solid"
                        bg="white"
                        color="gray.800"
                        borderWidth="1px"
                        borderColor="gray.400"
                        p={1}
                      >
                        <TagLabel fontSize="xs" whiteSpace="nowrap" fontFamily="mono">
                          <Box as="span" fontWeight="500" color="gray.900">
                            {filter?.kg_column_header?.name}
                          </Box>
                          <Box as="span" fontWeight="600" color="gray.800">
                            {" "}{getOperatorSymbol(filter?.operator!)}{" "}
                            {filter?.values?.length > 0
                              ? filter.values.map((v: any) => v.toString().trim()).join(", ")
                              : filter?.date_filter?.absolute_date_filter
                                ? getDateFilterName(filter.date_filter.absolute_date_filter)
                                : filter?.date_filter
                                  ? getDateFilterName(filter.date_filter)
                                  : ""}
                          </Box>
                        </TagLabel>
                      </Tag>
                    ))}
                    {displayedData.expression_filters?.map((filter, i) => (
                      <Tag
                        key={i}
                        size="xs"
                        borderRadius="md"
                        variant="solid"
                        bg="white"
                        color="gray.800"
                        borderWidth="1px"
                        borderColor="gray.300"
                        p={1}
                      >
                        <HStack spacing={1}>
                          <Icon as={TbMathFunction} boxSize={3.5} color="purple.500" flexShrink={0} />
                          <TagLabel fontSize="2xs" whiteSpace="nowrap" fontFamily="mono" fontWeight="600">
                            {filter}
                          </TagLabel>
                        </HStack>
                      </Tag>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}
            {isRecomputing ? (
              <Flex justify="center" align="center" h="120px">
                <Spinner size="md" color="purple.600" thickness="2px" />
              </Flex>
            ) : (
              <FormattedPandasTable
                  df={displayedData.pandas_dataframe}
                  setNumberOfRows={setNumberOfRows}
              />
            )}
            {aggregateColumns.length > 0 && (
              <Box p={2} bg="purple.50" borderTopWidth="1px" borderColor="gray.200">
                <HStack spacing={2} flexWrap="wrap">
                  {aggregateColumns.map((aggCol, index) => (
                    <Box key={index}>
                      <Text fontSize="sm" fontWeight="medium">
                        <Tooltip
                          label={aggCol.eval_expression || "No expression available"}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                        >
                          <Text as="span" color="gray.700" cursor="pointer">
                            {aggCol.name}
                          </Text>
                        </Tooltip>
                        <Text as="span" color="gray.700" mx={1}>=</Text>
                        <Text as="span" color="purple.600" fontWeight="semibold">
                          {aggCol.value}
                        </Text>
                      </Text>
                    </Box>
                  ))}
                </HStack>
              </Box>
            )}
          </VStack>
        )}

        {/* Analysis Plan Tab */}
        {activeTab === "plan" && (
          <Box px={1} py={1} pt={2}>
            <DataFramePlan dataFrame={data} type="query" />
            <DataFramePlan dataFrame={data} type="calculations" />

            {(data.expression_filters ?? []).length > 0 && (
              <Box px={2} py={1}>
                <HStack align="flex-start" spacing={2} py={1.5} borderBottom="1px solid" borderColor="gray.100">
                  <HStack spacing={1.5} flexShrink={0} minW="130px">
                    <Icon as={PiIntersectFill} boxSize={3.5} color="purple.500" />
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={0} whiteSpace="nowrap">
                      Only show
                    </Text>
                  </HStack>
                  <VStack align="stretch" spacing={0}>
                    {(data.expression_filters ?? []).map((filter, i) => {
                      const isActive = checkedExpressionFilters[filter] !== false;
                      return (
                        <HStack key={i} spacing={1}>
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            fontWeight="600"
                            color={isActive ? "gray.800" : "gray.400"}
                            textDecoration={isActive ? "none" : "line-through"}
                            mb={0}
                          >
                            {filter}
                          </Text>
                          <Button
                            leftIcon={<Icon as={isActive ? MdRemove : MdAdd} boxSize={4} />}
                            size="2xs"
                            variant="ghost"
                            colorScheme={isActive ? "red" : "gray"}
                            aria-label={isActive ? "Remove filter" : "Restore filter"}
                            onClick={() =>
                              setCheckedExpressionFilters((prev) => ({ ...prev, [filter]: !isActive }))
                            }
                          >
                            {isActive ? "Remove" : "Add"}
                          </Button>
                        </HStack>
                      );
                    })}
                    {hasExpressionFiltersChanged && (
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="solid"
                        mt={2}
                        onClick={handleGetData}
                        isLoading={isRecomputing}
                        loadingText="Recomputing..."
                      >
                        Recompute
                      </Button>
                    )}
                  </VStack>
                </HStack>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DataFrameDisplayComponent;
